import Skeleton from '@cvent/carina/components/Skeleton';
import {Card} from '../card/Card';
import {useSkeletonDelay} from './useSkeletonDelay';

interface FormSkeletonProps {
  /**
   * Skip the 300ms delay and show skeleton immediately
   * Use this for data-loading states after Suspense to prevent remount flicker
   */
  skipDelay?: boolean;
}

/**
 * Skeleton for form-based pages (CreateAccount, CreatePassword, ForgotPassword, etc.)
 * Includes a 300ms delay to prevent flash on fast-loading pages (unless skipDelay is true)
 */
export const FormSkeleton = ({skipDelay = false}: FormSkeletonProps) => {
  const show = useSkeletonDelay(skipDelay);

  if (!show) {
    return null;
  }

  return (
    <div
      className="flex justify-center items-start md:[margin-top:var(--carina-spacing-12)]"
      data-cvent-id="form-skeleton"
    >
      <Card styleClasses="p-4 pt-0 px-6 sm:p-8 sm:pl-6 md:pl-8 w-full max-w-[677px]">
        {/* Back button skeleton */}
        <div className="[margin-bottom:var(--carina-spacing-8)]">
          <Skeleton className="w-[80px] h-[32px] rounded" hasShimmer />
        </div>

        {/* Title skeleton */}
        <div className="[margin-bottom:var(--carina-spacing-8)]">
          <Skeleton className="w-[250px] h-[28px] rounded" hasShimmer />
        </div>

        {/* Form fields skeleton (4 rows x 2 columns) */}
        <div className="space-y-6">
          {Array.from({length: 4}).map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4">
              <div className="flex-1">
                <Skeleton
                  className="w-[120px] h-[16px] rounded mb-2"
                  hasShimmer
                />
                <Skeleton className="w-full h-[40px] rounded" hasShimmer />
              </div>
              <div className="flex-1">
                <Skeleton
                  className="w-[120px] h-[16px] rounded mb-2"
                  hasShimmer
                />
                <Skeleton className="w-full h-[40px] rounded" hasShimmer />
              </div>
            </div>
          ))}
        </div>

        {/* Button skeleton */}
        <div className="flex justify-center items-center [margin-top:var(--carina-spacing-8)]">
          <Skeleton className="w-[300px] h-[40px] rounded" hasShimmer />
        </div>
      </Card>
    </div>
  );
};
